<template>
  <div
    class="card h-100 border-0 running-game-card overflow-hidden"
    :class="isDarkMode ? 'running-game-card-dark' : 'running-game-card-light'"
    role="link"
    tabindex="0"
    @click="onCardClick"
    @keydown.enter="onCardClick"
  >
    <div class="card-body p-4 pb-4 d-flex flex-column">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <span
          class="badge rounded-pill px-3 py-1.5 fw-bold text-uppercase"
          :class="isDarkMode ? 'badge-live-dark' : 'badge-live-light'"
        >
          Běžící hra
        </span>
        <span
          v-if="rewardName"
          class="badge rounded-pill px-3 py-1.5 fw-bold"
          :class="isDarkMode ? 'badge-prize-dark' : 'badge-prize-light'"
        >
          {{ rewardName }}
        </span>
      </div>

      <div class="row align-items-start g-3 mb-0">
        <div class="col-6">
          <h4 class="game-title mb-2">{{ gameName }}</h4>
          <div v-if="gameText" class="game-text mb-3" v-html="gameText"></div>
          <div class="duration-frame mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="duration-heading">Trvání hry</span>
              <span class="duration-pct">{{ durationPct }}&nbsp;%</span>
            </div>
            <div class="progress duration-track">
              <div
                class="progress-bar duration-bar"
                role="progressbar"
                :style="{ width: durationPct + '%' }"
              ></div>
            </div>
            <div class="d-flex justify-content-between align-items-center mt-2">
              <span class="duration-label">{{ fromLabel }}</span>
              <span class="duration-label">{{ toLabel }}</span>
            </div>
          </div>
          <div class="row g-0 mx-0 finance-box">
            <div class="col-6 px-2 py-2 text-center border-end finance-col">
              <span class="finance-label">Obchodní kapitál</span>
              <span class="finance-value finance-capital">{{ formatNumber(gameCapital) }}</span>
            </div>
            <div class="col-6 px-2 py-2 text-center">
              <span class="finance-label">Vstupní vklad</span>
              <span class="finance-value">{{ formatNumber(gameCredit) }}</span>
            </div>
          </div>
        </div>
        <div class="col-6">
          <IngaPortrait
            class="reward-photo"
            frame="reward"
            :src="apiRoot + gameRewardImg"
            :alt="rewardName || 'Výhra'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import IngaPortrait from "./IngaPortrait.vue";
import moment from "moment";

const props = defineProps({
  gameId: Number,
  gameFrom: String,
  gameTo: String,
  gameName: String,
  gameText: String,
  gameCredit: String,
  gameCapital: String,
  gameRewardImg: String,
  rewardName: String
});

const store = useStore();
const router = useRouter();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const now = ref(moment());
let timer = null;

const apiRoot = computed(() => process.env.VUE_APP_API_URL);

const parseFrom = () => moment(String(props.gameFrom || "").trim(), ["DD.MM.YYYY HH:mm:ss", "DD.MM.YYYY"]);
const parseTo = () => moment(String(props.gameTo || "").trim(), ["DD.MM.YYYY HH:mm:ss", "DD.MM.YYYY"]);

const fromLabel = computed(() => parseFrom().isValid() ? parseFrom().format("D. M. YYYY") : "");
const toLabel = computed(() => parseTo().isValid() ? parseTo().format("D. M. YYYY") : "");

const durationPct = computed(() => {
  const from = parseFrom();
  const to = parseTo();
  if (!from.isValid() || !to.isValid()) return 0;
  const total = to.diff(from, "seconds");
  if (total <= 0) return 100;
  const elapsed = now.value.diff(from, "seconds");
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
});

const formatNumber = (number) => store.getters.formatNumber(number);

const loadRunningGameDetail = (gameId) => {
  router.push({ name: "Běžící hra", params: { gid: gameId } });
};

const onCardClick = (event) => {
  if (event?.target?.closest?.(".is-zoomable")) return;
  loadRunningGameDetail(props.gameId);
};

onMounted(() => {
  timer = setInterval(() => {
    now.value = moment();
  }, 30000);
});

onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
.running-game-card {
  border-radius: 1rem !important;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease;
}

.running-game-card:hover {
  transform: translateY(-3px);
}

.running-game-card-light {
  background: #ffffff;
  border: 1px solid #E2E8F0 !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.running-game-card-dark {
  background: #0F172A !important;
  border: 1px solid #334155 !important;
  color: #E2E8F0;
  box-shadow: none !important;
}

.badge-live-light {
  background: rgba(255, 122, 0, 0.14);
  color: #C2410C;
  font-size: 0.68rem;
  letter-spacing: 0.5px;
}

.badge-live-dark {
  background: rgba(255, 122, 0, 0.15);
  color: #FF8A4C;
  border: 1px solid rgba(255, 122, 0, 0.25);
  font-size: 0.68rem;
  letter-spacing: 0.5px;
}

.badge-prize-light {
  background: #F1F5F9;
  color: #475569;
  font-size: 0.68rem;
}

.badge-prize-dark {
  background: #1E293B;
  color: #E2E8F0;
  border: 1px solid #334155;
  font-size: 0.68rem;
}

.game-title {
  font-weight: 900;
  font-size: 1.35rem;
  letter-spacing: -0.03em;
  line-height: 1.2;
  color: #FF6B21;
}

.running-game-card-dark .game-title {
  color: #FF8A4C;
}

.game-text {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #64748B;
}

.game-text :deep(p) {
  margin-bottom: 0.6rem;
}

.game-text :deep(p:last-child) {
  margin-bottom: 0;
}

.game-text :deep(div),
.game-text :deep(p),
.game-text :deep(span),
.game-text :deep(li),
.game-text :deep(strong),
.game-text :deep(b),
.game-text :deep(em) {
  color: inherit;
}

.running-game-card-dark .game-text {
  color: #CBD5E1;
}

.finance-box {
  border-radius: 0.85rem;
  overflow: hidden;
}

.running-game-card-light .finance-box {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.running-game-card-dark .finance-box {
  background: #1E293B;
  border: 1px solid #334155;
}

.running-game-card-dark .finance-col {
  border-color: #334155 !important;
}

.finance-label {
  display: block;
  text-transform: uppercase;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748B;
}

.running-game-card-dark .finance-label {
  color: #94A3B8;
}

.finance-value {
  display: block;
  margin-top: 0.35rem;
  font-size: 1.15rem;
  font-weight: 900;
  color: #0F172A;
}

.running-game-card-dark .finance-value {
  color: #E2E8F0;
}

.finance-capital {
  color: #059669 !important;
}

.running-game-card-dark .finance-capital {
  color: #34D399 !important;
}

.duration-frame {
  padding: 12px 14px 10px;
  border-radius: 0.85rem;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
}

.running-game-card-dark .duration-frame {
  background: #1E293B;
  border-color: #64748B;
}

.duration-heading {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748B;
}

.running-game-card-dark .duration-heading {
  color: #94A3B8;
}

.duration-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748B;
}

.running-game-card-dark .duration-label {
  color: #94A3B8;
}

.duration-pct {
  font-size: 0.75rem;
  font-weight: 800;
  color: #FF6B21;
}

.running-game-card-dark .duration-pct {
  color: #FF8A4C;
}

.duration-track {
  height: 10px;
  border-radius: 999px;
  background: #E2E8F0;
  overflow: hidden;
}

.running-game-card-dark .duration-track {
  background: #0F172A;
}

.duration-bar {
  background: linear-gradient(90deg, #FF7A00 0%, #FF4D00 100%);
  border-radius: 999px;
}

.reward-photo {
  width: 100%;
  max-width: 100%;
}

.running-game-card-light:hover {
  box-shadow: 0 15px 30px rgba(15, 23, 42, 0.1);
}

.running-game-card-dark:hover {
  border-color: rgba(255, 122, 0, 0.4) !important;
  box-shadow: 0 12px 24px rgba(255, 77, 0, 0.12) !important;
}
</style>
